import React, { useState } from "react";
import { ArrowDownCircle, ArrowUpCircle, CalendarClock } from "lucide-react";
import moment from "moment";
import toast from "react-hot-toast";

export default function TransactionCard({ item }) {
  const [previewVisible, setPreviewVisible] = useState(false);

  const isDeposit = !!item.payment_id;
  const isWithdrawal = !!item.withdrawal_id;

  const statusConfig = {
    0: {
      text: "PROCESSING",
      color: "bg-yellow-500",
      colortext: "text-yellow-500",
    },
    1: {
      text: "SUCCESS",
      color: "bg-green-600",
      colortext: "text-green-600",
    },
    2: {
      text: "REJECTED",
      color: "bg-red-600",
      colortext: "text-red-600",
    },
  };

  const status = statusConfig[item.status] || statusConfig[0];

  const handleStatusClick = () => {
    if (item.status == 0) toast("Transaction is processing...");
    if (item.status == 1) toast.success("Transaction successful!");
    if (item.status == 2) toast.error("Transaction rejected!");
  };

  return (
    <>
      <div className="bg-[var(--background)] border border-[var(--hover)] rounded-xl p-4 mt-3 shadow-sm transition-all hover:shadow-md">
        {/* ================= HEADER ================= */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <div
              className={`p-2 rounded-full ${
                isDeposit
                  ? "bg-green-100 text-green-600"
                  : "bg-red-100 text-red-600"
              }`}
            >
              {isDeposit ? (
                <ArrowDownCircle size={18} />
              ) : (
                <ArrowUpCircle size={18} />
              )}
            </div>

            <div className="flex items-center gap-2">
              <div>
                <h2 className="text-[var(--text)] font-semibold text-sm">
                  {isDeposit ? "Deposit" : "Withdrawal"}
                </h2>
                <p className="text-xs text-[var(--muted)]">
                  ID: {item.payment_id || item.withdrawal_id}
                </p>
              </div>

              {/* Withdrawal Method Tag */}
              {isWithdrawal && (
                <span className="text-[10px] text-[var(--text)] px-2 py-1 rounded bg-[var(--hover)]/10 text-[var(--hover)]">
                  {item.upi ? "UPI" : "BANK"}
                </span>
              )}
            </div>
          </div>

          <button
            onClick={handleStatusClick}
            className={`text-xs px-3 py-1 rounded-md text-white ${status.color}`}
          >
            {status.text}
          </button>
        </div>

        {/* ================= BODY ================= */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-y-3 text-sm">
          <InfoRow
            label="Amount"
            value={`₹ ${Number(item.amount || 0).toLocaleString("en-IN")}`}
          />

          <InfoRow
            label="Message"
            value={item.msg || "-"}
            color={status.colortext}
          />
          <InfoRow
            label="UTR ID"
            value={item.utr || "-"}
          />

          {/* Withdrawal Details */}
          {isWithdrawal && (
            <>
              {item.upi ? (
                <InfoRow label="UPI ID" value={item.upi} />
              ) : (
                <>
                  <InfoRow
                    label="Account No."
                    value={item.account_number || "-"}
                  />
                  <InfoRow
                    label="Account Name"
                    value={item.account_name || "-"}
                  />
                  <InfoRow label="IFSC Code" value={item.account_ifsc || "-"} />
                </>
              )}
            </>
          )}

          <InfoRow
            label="Date"
            value={moment(item.date_created).format("DD MMM YYYY, hh:mm A")}
          />
        </div>

        {/* ================= DEPOSIT SLIP ================= */}
        {isDeposit && item.base64 && (
          <div className="mt-3">
            <button
              onClick={() => setPreviewVisible(true)}
              className="text-blue-500 text-xs underline"
            >
              View Deposit Slip
            </button>
          </div>
        )}

        {/* ================= FOOTER ================= */}
        <div className="flex items-center justify-end mt-4 text-[var(--muted)] text-xs">
          <CalendarClock size={13} className="mr-1" />
          Logged on {moment(item.date_created).format("DD MMM YYYY")}
        </div>
      </div>

      {/* ================= SLIP PREVIEW MODAL ================= */}
      {previewVisible && (
        <div
          onClick={() => setPreviewVisible(false)}
          className="fixed inset-0 bg-black/80 flex justify-center items-center z-50"
        >
          <img
            src={`data:${item.mime_type};base64,${item.base64}`}
            alt="Deposit Slip"
            className="max-w-[90%] max-h-[90%] rounded-xl shadow-2xl"
          />
        </div>
      )}
    </>
  );
}

function InfoRow({ label, value, color }) {
  return (
    <div className="flex flex-col">
      <span className="text-[var(--text)] text-[11px] uppercase tracking-wide">
        {label}
      </span>
      <span
        className={`${color || "text-[var(--text)]"} font-medium font-semibold text-sm break-words`}
      >
        {value}
      </span>
    </div>
  );
}
